/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag jsx
 */
import React, { useEffect, useState } from 'react';

import { useIntl } from 'react-intl';

import { cssMap, jsx } from '@atlaskit/css';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import Image from '@atlaskit/image';
import { Box, Pressable, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import type { AssistanceServiceInputParam } from '@atlassian/conversation-assistant-service/services/assistance-service/main';
import RovoPromptFilterDropdown, {
	RovoFilterStyleButton,
} from '@atlassian/rovo-conversation-actions/filter-prompts';
import { messages as filterPromptsMessages } from '@atlassian/rovo-conversation-actions/filter-prompts-messages';
import {
	type PromptCard,
	roleOptions,
	useCaseOptions,
} from '@atlassian/rovo-conversation-actions/prompt-cards';
import { PromptCardsGrid } from '@atlassian/rovo-conversation-actions/prompt-cards-grid';
import { PromptCardsMultiselectToolbar } from '@atlassian/rovo-conversation-actions/prompt-library-toolbar';
import { RovoConversationSearchPrompts } from '@atlassian/rovo-conversation-actions/search-prompts';
import type { FilterOption } from '@atlassian/rovo-conversation-actions/types';
import type { SkillMetadataMap } from '@atlassian/rovo-skills/types';

import promptLibraryMessages from '../../../../ui/prompt-library/messages';
import BuildIconNoPrompts from '../assets/build-icon-no-prompts.svg';
import FavoritesEmpty from '../assets/favorites-empty.svg';

import { PromptCardsGridSkeleton } from './prompt-cards-grid-skeleton';

const styles = cssMap({
	searchSection: {
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.100'),
		marginBottom: token('space.200'),
		paddingLeft: token('space.100'),
		paddingRight: token('space.100'),
	},
	filterRow: {
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'center',
		paddingLeft: token('space.100'),
		paddingRight: token('space.100'),
		marginBottom: token('space.200'),
	},
	filterContainer: {
		display: 'flex',
		gap: token('space.100'),
	},
	counterContainer: {
		display: 'flex',
		alignItems: 'center',
		gap: token('space.400'),
	},
	clearFiltersButton: {
		backgroundColor: 'transparent',
		border: 'none',
		paddingTop: token('space.075'),
		paddingRight: token('space.075'),
		paddingBottom: token('space.075'),
		paddingLeft: token('space.075'),
		borderRadius: token('radius.large'),
		cursor: 'pointer',

		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
		'&:active': {
			backgroundColor: token('color.background.neutral.subtle.pressed'),
		},
	},
	emptyPromptState: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		textAlign: 'center',
		gap: token('space.100'),
		paddingBlock: token('space.100'),
	},
	emptyPromptStateIcon: {
		display: 'flex',
		justifyContent: 'center',
	},
});

export type PromptLibraryCategory = 'all' | 'myPrompts' | 'favorites';

export type PromptLibraryContentProps = {
	searchablePrompts: string[];
	onSearchChange: (query: string, filtered: string[]) => void;
	selectedCategory: PromptLibraryCategory;
	selectedCategoryFilters: Set<Exclude<PromptLibraryCategory, 'all'>>;
	onMyPromptsClick: () => void;
	onFavoritesClick: () => void;
	showMyPromptsButton: boolean;
	showFavoritesButton: boolean;
	selectedUseCases: string[];
	onUseCaseFilterChange: (filters: string[]) => void;
	selectedRoles: string[];
	onRoleFilterChange: (filters: string[]) => void;
	resultCount: number;
	hasActiveFilters: boolean;
	onClearFilters: () => void;
	cardsToShow: PromptCard[];
	onCardClick: (card: PromptCard) => void;
	onCardHover?: (card: PromptCard | null) => void;
	onDeletePrompt?: (promptId: string) => Promise<void>;
	onEditPrompt?: (card: PromptCard) => void;
	onDuplicatePrompt?: (card: PromptCard) => void;
	onFavoriteToggle?: (
		promptId: string,
		isFavorited: boolean,
		isUserGenerated?: boolean,
	) => Promise<void>;
	/** Bulk favorite handler for multiselect - receives array of {shareId, isFavorite} items and systemPromptCount for analytics */
	onBulkFavorite?: (
		prompts: { shareId: string; isFavorite: boolean }[],
		systemPromptCount: number,
	) => Promise<void>;
	/** Bulk delete handler - deletes multiple prompts and refetches the list */
	onBulkDelete?: (promptIds: string[], systemPromptCount: number) => Promise<void>;
	/** Bulk share handler - makes prompts public before copying link. Called with prompt IDs and systemPromptCount for analytics. */
	onBulkShare?: (promptIds: string[], systemPromptCount: number) => Promise<void>;
	onSharePrompt?: (card: PromptCard) => void;
	assistanceServiceParams?: AssistanceServiceInputParam['assistanceServiceParams'];
	skillMetadata?: SkillMetadataMap;
	connectorFilter?: {
		options: FilterOption[];
		selected: string[];
		onChange: (filters: string[]) => void;
		renderIcon?: (option: FilterOption) => React.ReactNode;
	};
	renderUseCaseOptionIcon?: (option: FilterOption) => React.ReactNode;
	/** Fired when the user views an empty state (empty favorites, empty my prompts, or no results from filters) */
	onEmptyStateViewed?: (
		emptyStateType: 'emptyFavorites' | 'emptyMyPrompts' | 'noPromptsFromFilters',
	) => void;
	isLoading?: boolean;
};

export const PromptLibraryContent = ({
	searchablePrompts,
	onSearchChange,
	selectedCategory,
	selectedCategoryFilters,
	onMyPromptsClick,
	onFavoritesClick,
	showMyPromptsButton,
	showFavoritesButton,
	selectedUseCases,
	onUseCaseFilterChange,
	selectedRoles,
	onRoleFilterChange,
	resultCount,
	hasActiveFilters,
	onClearFilters,
	cardsToShow,
	onCardClick,
	onCardHover,
	onDeletePrompt,
	onEditPrompt,
	onDuplicatePrompt,
	onFavoriteToggle,
	onBulkFavorite,
	onBulkDelete,
	onBulkShare,
	onSharePrompt,
	assistanceServiceParams,
	skillMetadata,
	connectorFilter,
	renderUseCaseOptionIcon,
	onEmptyStateViewed,
	isLoading,
}: PromptLibraryContentProps): JSX.Element => {
	const { formatMessage } = useIntl();
	const [selectedCardIds, setSelectedCardIds] = useState<Set<string>>(() => new Set());

	useEffect(() => {
		// Don't fire empty state analytics while loading
		if (isLoading || resultCount !== 0 || !onEmptyStateViewed) {
			return;
		}
		if (selectedCategory === 'favorites') {
			onEmptyStateViewed('emptyFavorites');
		} else if (selectedCategory === 'myPrompts') {
			onEmptyStateViewed('emptyMyPrompts');
		} else if (selectedCategory === 'all' && hasActiveFilters) {
			onEmptyStateViewed('noPromptsFromFilters');
		}
	}, [resultCount, selectedCategory, hasActiveFilters, onEmptyStateViewed, isLoading]);

	return (
		<>
			<Box xcss={styles.searchSection}>
				<RovoConversationSearchPrompts
					prompts={searchablePrompts}
					onSearchChange={onSearchChange}
				/>
			</Box>

			<Box xcss={styles.filterRow}>
				<Box xcss={styles.filterContainer}>
					{showFavoritesButton && (
						<RovoFilterStyleButton
							isSelected={selectedCategoryFilters.has('favorites')}
							onClick={onFavoritesClick}
						>
							<Text
								as="span"
								weight="medium"
								color={
									selectedCategoryFilters.has('favorites')
										? 'color.text.selected'
										: 'color.text.subtlest'
								}
							>
								{formatMessage(promptLibraryMessages.favorites)}
							</Text>
						</RovoFilterStyleButton>
					)}
					{showMyPromptsButton && (
						<RovoFilterStyleButton
							isSelected={selectedCategoryFilters.has('myPrompts')}
							onClick={onMyPromptsClick}
						>
							<Text
								as="span"
								weight="medium"
								color={
									selectedCategoryFilters.has('myPrompts')
										? 'color.text.selected'
										: 'color.text.subtlest'
								}
							>
								{formatMessage(promptLibraryMessages.myPrompts)}
							</Text>
						</RovoFilterStyleButton>
					)}
					{connectorFilter && connectorFilter.options.length > 0 && (
						<RovoPromptFilterDropdown
							filterOptions={connectorFilter.options}
							selectedFilters={connectorFilter.selected}
							onFilterChange={connectorFilter.onChange}
							triggerText={formatMessage(filterPromptsMessages.connectorsFilterTrigger)}
							title={formatMessage(filterPromptsMessages.connectorsFilterTitle)}
							filterType="connector"
							renderIcon={connectorFilter.renderIcon}
						/>
					)}
					<RovoPromptFilterDropdown
						filterOptions={useCaseOptions}
						selectedFilters={selectedUseCases}
						onFilterChange={onUseCaseFilterChange}
						triggerText={formatMessage(filterPromptsMessages.useCasesFilterTrigger)}
						title={formatMessage(filterPromptsMessages.useCasesFilterTitle)}
						filterType="useCase"
						renderIcon={renderUseCaseOptionIcon}
					/>
					<RovoPromptFilterDropdown
						filterOptions={roleOptions}
						selectedFilters={selectedRoles}
						onFilterChange={onRoleFilterChange}
						triggerText={formatMessage(filterPromptsMessages.rolesFilterTrigger)}
						title={formatMessage(filterPromptsMessages.rolesFilterTitle)}
						filterType="role"
					/>
				</Box>

				{!FeatureGates.getExperimentValue(
					'rovo_chat_prompt_library_v2_exp',
					'isEnabled',
					false,
				) && (
					<Text color="color.text.subtle" size="medium">
						{formatMessage(filterPromptsMessages.resultsCount, {
							count: resultCount,
						})}
					</Text>
				)}
				{hasActiveFilters && (
					<Pressable onClick={onClearFilters} xcss={styles.clearFiltersButton}>
						<Text color="color.text.subtle" size="medium">
							{formatMessage(filterPromptsMessages.clearFilters)}
						</Text>
					</Pressable>
				)}
			</Box>

			<Box>
				{isLoading ? (
					<PromptCardsGridSkeleton />
				) : selectedCategory === 'myPrompts' && resultCount === 0 ? (
					<Box xcss={styles.emptyPromptState}>
						<Box xcss={styles.emptyPromptStateIcon}>
							<Image src={BuildIconNoPrompts} alt="" />
						</Box>
						<Text weight="bold" size="large" as="p">
							{formatMessage(promptLibraryMessages.myPromptsEmptyTitle)}
						</Text>
						<Text color="color.text.subtle" size="medium" as="p">
							{formatMessage(promptLibraryMessages.myPromptsEmptyDescription)}
						</Text>
					</Box>
				) : selectedCategory === 'favorites' && resultCount === 0 ? (
					<Box xcss={styles.emptyPromptState}>
						<Box xcss={styles.emptyPromptStateIcon}>
							<Image src={FavoritesEmpty} alt="" />
						</Box>
						<Text weight="bold" size="large" as="p">
							{formatMessage(promptLibraryMessages.favoritesEmptyTitle)}
						</Text>
						<Text color="color.text.subtle" size="medium" as="p">
							{formatMessage(promptLibraryMessages.favoritesEmptyDescription)}
						</Text>
					</Box>
				) : (
					<>
						<PromptCardsGrid
							cards={cardsToShow}
							onCardClick={onCardClick}
							onCardHover={onCardHover}
							onDeletePrompt={onDeletePrompt}
							onEditPrompt={onEditPrompt}
							onDuplicatePrompt={onDuplicatePrompt}
							onFavoriteToggle={onFavoriteToggle}
							onSharePrompt={onSharePrompt}
							selectedCardIds={selectedCardIds}
							onSelectionChange={setSelectedCardIds}
							assistanceServiceParams={assistanceServiceParams}
							skillMetadata={skillMetadata}
						/>
						{selectedCardIds.size > 0 && (
							<PromptCardsMultiselectToolbar
								selectedCount={selectedCardIds.size}
								selectedCards={cardsToShow.filter((c) => selectedCardIds.has(c.id))}
								onClearSelection={() => setSelectedCardIds(new Set())}
								onBulkFavorite={onBulkFavorite}
								onBulkDelete={onBulkDelete}
								onBulkShare={onBulkShare}
								onDeletePrompt={onDeletePrompt}
							/>
						)}
					</>
				)}
			</Box>
		</>
	);
};
